import React from "react";
import { Box } from "@mui/material";

export const C = {
  ink: "var(--color-ink)",
  muted: "var(--color-accent-violet-mid)",
  line: "var(--color-hairline-cloud)",
  lineStrong: "var(--color-hairline-cool)",
  surface: "var(--color-surface-canvas-light)",
  hover: "var(--color-surface-press-light)",
  success: "var(--color-success)",
  warn: "var(--color-warn)",
  danger: "var(--color-danger)",
  primary: "var(--color-primary)",
  violet: "var(--color-accent-violet)",
};

export function Icon({ size = 14, children }) {
  return (
    <Box component="svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" sx={{ flex: "0 0 auto" }}>
      {children}
    </Box>
  );
}

export const CloseIcon = () => <Icon><path d="M18 6 6 18M6 6l12 12" /></Icon>;
export const PeopleIcon = ({ size = 13 }) => (
  <Icon size={size}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8" />
  </Icon>
);

export const outlineBtn = {
  display: "inline-flex", alignItems: "center", gap: "7px", height: 30, px: "10px", borderRadius: "8px",
  border: `1px solid ${C.lineStrong}`, bgcolor: "#fff", font: "600 11px var(--font-ui)", letterSpacing: ".2px",
  textTransform: "uppercase", color: C.ink, cursor: "pointer", whiteSpace: "nowrap",
  "&:hover": { borderColor: C.ink, bgcolor: C.hover },
  "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
};

export const primaryBtn = {
  ...outlineBtn, bgcolor: C.primary, color: "#fff", borderColor: C.primary,
  "&:hover": { bgcolor: "var(--color-ink-deep)", borderColor: "var(--color-ink-deep)" },
  "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
};

export function NumberStepper({ value, min, max, label, onChange }) {
  const btn = {
    width: 30, height: "100%", border: 0, bgcolor: "#fff", cursor: "pointer", color: C.ink,
    font: "600 15px var(--font-ui)", "&:hover": { bgcolor: C.hover },
    "&:disabled": { opacity: 0.35, cursor: "default" },
  };
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", height: 32, border: `1px solid ${C.lineStrong}`,
      borderRadius: "8px", overflow: "hidden", flex: "0 0 auto" }}>
      <Box component="button" type="button" aria-label={`Fewer ${label}`} disabled={value <= min}
        onClick={() => onChange(value - 1)} sx={btn}>−</Box>
      <Box component="output" aria-live="polite" aria-label={`${label} count`}
        sx={{ minWidth: 30, textAlign: "center", font: "600 13px var(--font-mono)", lineHeight: "30px",
          borderLeft: `1px solid ${C.line}`, borderRight: `1px solid ${C.line}` }}>{value}</Box>
      <Box component="button" type="button" aria-label={`More ${label}`} disabled={value >= max}
        onClick={() => onChange(value + 1)} sx={btn}>+</Box>
    </Box>
  );
}

/** A selectable card: radio (one of a group) or checkbox (many). */
export function ChoiceCard({ kind = "radio", checked, title, description, onClick, testId }) {
  const mark = kind === "radio"
    ? { borderRadius: "50%", "&::after": checked ? { content: '""', width: 8, height: 8, borderRadius: "50%", bgcolor: C.ink } : {} }
    : { borderRadius: "4px", bgcolor: checked ? C.ink : "transparent", color: "#fff" };
  return (
    <Box component="button" type="button" role={kind} aria-checked={checked} data-testid={testId} onClick={onClick}
      sx={{ display: "grid", gridTemplateColumns: "18px minmax(0,1fr)", gap: "12px", alignItems: "start", p: "14px",
        border: `1px solid ${checked ? C.ink : C.lineStrong}`, boxShadow: checked ? `0 0 0 1px ${C.ink}` : "none",
        borderRadius: "12px", cursor: "pointer", textAlign: "left", bgcolor: "#fff", font: "inherit", color: "inherit",
        transition: "border-color .15s", "&:hover": { borderColor: checked ? C.ink : C.muted } }}>
      <Box component="span" sx={{ width: 18, height: 18, mt: "1px", display: "grid", placeItems: "center",
        border: `1.5px solid ${checked ? C.ink : C.lineStrong}`, ...mark }}>
        {kind === "checkbox" && checked && (
          <Icon size={12}><path d="M20 6 9 17l-5-5" /></Icon>
        )}
      </Box>
      <Box component="span">
        <Box component="b" sx={{ display: "block", fontWeight: 600, fontSize: 14 }}>{title}</Box>
        {description && (
          <Box component="small" sx={{ display: "block", fontSize: 12.5, color: C.muted, mt: "2px" }}>{description}</Box>
        )}
      </Box>
    </Box>
  );
}

/** Two-or-more option segmented control, e.g. "Unlimited / Set a limit". */
export function Segmented({ label, options, value, onChange }) {
  return (
    <Box role="group" aria-label={label}
      sx={{ display: "flex", p: "3px", borderRadius: "8px", bgcolor: "#fff", border: `1px solid ${C.lineStrong}` }}>
      {options.map(([key, text]) => (
        <Box key={key} component="button" type="button" aria-pressed={key === value} onClick={() => onChange(key)}
          sx={{ height: 28, px: "12px", border: 0, borderRadius: "6px", font: "500 12.5px var(--font-ui)", cursor: "pointer",
            whiteSpace: "nowrap", bgcolor: key === value ? C.primary : "transparent", color: key === value ? "#fff" : C.muted }}>
          {text}
        </Box>
      ))}
    </Box>
  );
}
